/**
 * Piece bench (?gallery=1, USER diagnostic request): every WFC variant
 * of every track piece, built through the REAL pipeline — the same
 * skeleton → assemble → collision proxies → rail law the game runs, at
 * the slalom scale — so the three truths (mesh, collision, rail) can be
 * compared side by side per piece.
 *
 * - C / [collision] button: toggle collision-proxy wireframes + rail
 *   station dots over every plot.
 * - Click a plot to SELECT it; [simulate] rains physics marbles down
 *   the selected variant's channel (raw Box3D, no autopilot — geometry
 *   answers honestly); [copy info] puts a shareable diagnostic JSON on
 *   the clipboard (variant, mesh bbox, proxies, rail ports).
 *
 * Found-by-bench so far (user reports): bridge deck visuals sometimes
 * higher than collision; some turn pieces' LEFT edge mismatched.
 */
import * as THREE from "three";
import type { Assets } from "./assets.ts";
import type { SceneCtx } from "./scene.ts";
import { createCameras } from "./camera.ts";
import { fitShadowsTo } from "./scene.ts";
import { buildRegistry, type PieceDef } from "../gen/registry.ts";
import type { Skeleton, SkeletonCell } from "../gen/topology.ts";
import type { ElevationPlan } from "../gen/elevation.ts";
import { assemble } from "../gen/assemble.ts";
import { pickStyle } from "../gen/style.ts";
import { rngFamily } from "../gen/rng.ts";
import { DIR_STEP, turnLeft, turnRight } from "../gen/geom.ts";
import { type CollisionProxy, segmentProxies } from "../gen/endless/proxies.ts";
import { scaleSeg, scaleVec, TRACK_SCALE } from "../gen/endless/scale.ts";
import { instantiatePlacements, type Spinner } from "./track.ts";
import { installCua } from "./cua.ts";
import { type Rail, railForSegment } from "./physics/rail.ts";
import type { LoopPath } from "../shared/types.ts";
import {
  MARBLE_RADIUS,
  PhysicsEngine,
  type PhysicsModule,
} from "./physics/engine.ts";
import { autopilotForce, pilotFor } from "./physics/autopilot.ts";
import type { Dir } from "../shared/types.ts";

interface Plot {
  def: PieceDef;
  group: THREE.Group;
  origin: THREE.Vector3;
  path: LoopPath;
  rail: Rail;
  proxies: CollisionProxy[];
  overlay: THREE.Group;
  labelSprite: THREE.Sprite | null;
  label: string;
  /** Finite, non-empty render bounds (T7 assertion). Phantoms pass via
   * their ghosted host mesh; false is a loud data defect. */
  meshValid: boolean;
}

function diagnosticInfo(p: Plot) {
  const box = new THREE.Box3().setFromObject(p.group);
  box.translate(p.origin.clone().negate());
  return {
    piece: p.def.id,
    variant: {
      reversed: !!p.def.reversed,
      mirrored: !!p.def.mirrored,
      moves: p.def.moves,
      family: p.def.family,
      deckDelta: p.def.deckDelta,
      floorIn: p.def.floorIn,
      floorOut: p.def.floorOut,
      heightSpan: p.def.heightSpan,
      meshOffset: p.def.meshOffset,
      meshRot: p.def.meshRot,
    },
    meshValid: p.meshValid,
    phantom: !!p.def.noMesh,
    meshBBox: {
      min: box.min.toArray().map((v: number) => +v.toFixed(3)),
      max: box.max.toArray().map((v: number) => +v.toFixed(3)),
    },
    rail: {
      stations: p.rail.pts.length,
      entry: p.rail.pts[0],
      exit: p.rail.pts[p.rail.pts.length - 1],
    },
    proxies: p.proxies.map((pr) =>
      pr.kind === "mesh"
        ? { kind: pr.kind, verts: pr.vertices.length / 3 }
        : pr.kind === "bumper"
        ? { kind: pr.kind, center: pr.center, r: pr.radius, h: pr.height }
        : {
          kind: pr.kind,
          center: pr.center,
          half: pr.half,
          ...(pr.kind === "slab" ? { yaw: pr.yaw, pitch: pr.pitch } : {}),
        }
    ),
    scale: TRACK_SCALE,
  };
}

/** Assemble ONE variant on a synthetic skeleton — the production path. */
function buildVariant(def: PieceDef): {
  placements: ReturnType<typeof assemble>["placements"];
  path: LoopPath;
} | null {
  const cells: SkeletonCell[] = [];
  let x = 0;
  let z = 0;
  let dir: Dir = 1; // entry frame: heading +z
  for (const m of def.moves) {
    const move = m as "F" | "L" | "R";
    const dirOut: Dir = move === "F"
      ? dir
      : move === "L"
      ? turnLeft(dir)
      : turnRight(dir);
    cells.push({ x, z, dirIn: dir, dirOut, move });
    dir = dirOut;
    x += DIR_STEP[dirOut][0];
    z += DIR_STEP[dirOut][1];
  }
  const skeleton: Skeleton = { cells, crossings: [], helixLoops: [] };
  // Step-at-start: interior cells carry the exit base (what realize/
  // assemble expect of reserved windows).
  const elevation: ElevationPlan = {
    baseY: cells.map((_, j) => (j === 0 ? 0 : def.deckDelta)),
    reservations: [],
    maxBaseY: Math.max(0, def.deckDelta),
  };
  const style = pickStyle(rngFamily("bench", "style"));
  try {
    const out = assemble(skeleton, elevation, style, [
      { start: 0, len: cells.length, def },
    ], { open: true });
    const path: LoopPath = {
      segments: out.loop.segments.map((s) => ({
        ...s,
        curve: s.curve.map(scaleSeg),
        length: s.length * TRACK_SCALE,
        // Floor-pierce centers scale with the track (LoopSegment law).
        ...(s.holes ? { holes: s.holes.map(scaleVec) } : {}),
        ...(s.alt
          ? {
            alt: {
              ...s.alt,
              curve: s.alt.curve.map(scaleSeg),
              length: s.alt.length * TRACK_SCALE,
            },
          }
          : {}),
      })),
      totalLength: out.loop.totalLength * TRACK_SCALE,
    };
    return {
      placements: out.placements.map((p) => ({
        ...p,
        pos: scaleVec(p.pos),
        scale: TRACK_SCALE,
      })),
      path,
    };
  } catch (err) {
    console.warn(`[bench] ${def.id} failed to assemble:`, err);
    return null;
  }
}

/** Collision proxies + rail as wireframe/dot overlays (world offset). */
function buildOverlay(
  proxies: readonly CollisionProxy[],
  rail: Rail,
): THREE.Group {
  const g = new THREE.Group();
  const wire = new THREE.MeshBasicMaterial({
    color: 0x27e07a,
    wireframe: true,
    transparent: true,
    opacity: 0.55,
    depthTest: false,
  });
  for (const p of proxies) {
    if (p.kind === "mesh") {
      const geo = new THREE.BufferGeometry();
      geo.setAttribute(
        "position",
        new THREE.BufferAttribute(new Float32Array(p.vertices), 3),
      );
      geo.setIndex([...p.indices]);
      g.add(new THREE.Mesh(geo, wire));
      continue;
    }
    if (p.kind === "bumper") {
      const geo = new THREE.CylinderGeometry(p.radius, p.radius, p.height, 10);
      const m = new THREE.Mesh(geo, wire);
      m.position.set(p.center[0], p.center[1] + p.height / 2, p.center[2]);
      g.add(m);
      continue;
    }
    const geo = new THREE.BoxGeometry(
      p.half[0] * 2,
      p.half[1] * 2,
      p.half[2] * 2,
    );
    const m = new THREE.Mesh(geo, wire);
    m.position.set(p.center[0], p.center[1], p.center[2]);
    if (p.kind === "slab") {
      m.rotation.set(p.pitch, p.yaw, 0, "YXZ");
    }
    g.add(m);
  }
  // Rail stations: cyan dots at the control law's centerline.
  const dots = new THREE.BufferGeometry();
  dots.setAttribute(
    "position",
    new THREE.BufferAttribute(
      new Float32Array(rail.pts.flatMap((p) => [p[0], p[1], p[2]])),
      3,
    ),
  );
  g.add(
    new THREE.Points(
      dots,
      new THREE.PointsMaterial({
        color: 0x4fd8ff,
        size: 0.09,
        depthTest: false,
      }),
    ),
  );
  return g;
}

export function buildPieceBench(ctx: SceneCtx, assets: Assets): void {
  const registry = buildRegistry();
  const root = new THREE.Group();
  root.name = "piece-bench";
  ctx.scene.add(root);

  const plots: Plot[] = [];
  const spacingX = 14;
  const spacingZ = 16;
  const cols = Math.ceil(Math.sqrt(registry.variants.length * 1.4));
  const spinners: Spinner[] = [];
  const missing = new Set<string>();

  registry.variants.forEach((def, i) => {
    const built = buildVariant(def);
    if (!built) return;
    const origin = new THREE.Vector3(
      (i % cols) * spacingX,
      0,
      Math.floor(i / cols) * spacingZ,
    );
    const group = new THREE.Group();
    group.position.copy(origin);
    instantiatePlacements(
      built.placements,
      assets,
      group,
      spinners,
      missing,
    );
    // Bench-evaluable phantoms (T7): a noMesh def (cross-over — a
    // crossing's second visit) places no mesh of its own, which left the
    // plot with NULL bounds, a stuck camera, and an unauditable row. Show
    // the HOST mesh the marble actually sees at that cell, ghosted and
    // perpendicular to the phantom's travel.
    if (def.noMesh && def.meshId) {
      const ghost = new THREE.Group();
      instantiatePlacements(
        [{
          pieceId: def.meshId,
          pos: [0, 0, 0],
          rotation: turnLeft(1),
          role: "track",
          scale: TRACK_SCALE,
        }],
        assets,
        ghost,
        spinners,
        missing,
      );
      ghost.traverse((o: THREE.Object3D) => {
        const mesh = o as THREE.Mesh;
        if (mesh.isMesh && !Array.isArray(mesh.material)) {
          const mat = mesh.material.clone();
          mat.transparent = true;
          mat.opacity = 0.45;
          mesh.material = mat;
        }
      });
      group.add(ghost);
    }
    root.add(group);
    // T7 assertion: every plot must have finite, non-empty mesh bounds —
    // a silent empty made an audit row not-evaluable.
    const bbox = new THREE.Box3().setFromObject(group);
    const meshValid = !bbox.isEmpty() &&
      Number.isFinite(bbox.min.x) && Number.isFinite(bbox.max.x);
    if (!meshValid) {
      console.error(`[bench] ${def.id}: empty/non-finite mesh bounds`);
    }

    // ONE segment source for rail AND proxies, fed to the same builder
    // production uses (T7 parity — the bench used to omit the wide
    // scales and lied about all five wide/funnel variants).
    const src = {
      path: built.path,
      pieceFamilies: [def.family],
      windowMods: [null],
      // Def-authored port widths: an ISOLATED funnel/wide window still
      // gets its true section (production derives the same from its
      // funnel-bracketed neighbors).
      widths: [{ widthIn: def.widthIn, widthOut: def.widthOut }],
    };
    const rail = railForSegment(src);
    const proxies = segmentProxies(src);
    const overlay = buildOverlay(proxies, rail);
    overlay.position.copy(origin);
    overlay.visible = false;
    root.add(overlay);

    const label = `${def.id}${def.reversed ? " ⟲rev" : ""}${
      def.mirrored ? " ⇋mir" : ""
    }`;
    const sprite = makeBenchLabel(label);
    if (sprite) {
      sprite.position.set(origin.x, -0.6, origin.z + 2.2);
      root.add(sprite);
    }
    plots.push({
      def,
      group,
      origin,
      path: built.path,
      rail,
      proxies,
      overlay,
      labelSprite: sprite,
      label,
      meshValid,
    });
  });
  for (const id of missing) console.warn(`[bench] missing mesh "${id}"`);

  // ---- selection + tools ---------------------------------------------------
  let selected: Plot | null = null;
  const marker = new THREE.Mesh(
    new THREE.RingGeometry(0.5, 0.62, 24),
    new THREE.MeshBasicMaterial({ color: 0xffd75e, depthTest: false }),
  );
  marker.rotation.x = -Math.PI / 2;
  marker.visible = false;
  root.add(marker);

  const ui = document.createElement("div");
  ui.className = "piece-bench-toolbar";
  ui.setAttribute("role", "toolbar");
  ui.setAttribute("aria-label", "Piece collision audit controls");
  Object.assign(ui.style, {
    position: "fixed",
    bottom: "14px",
    left: "50%",
    transform: "translateX(-50%)",
    display: "flex",
    gap: "8px",
    zIndex: "12",
    font: "600 12px ui-monospace, Menlo, monospace",
  });
  const mkBtn = (text: string, fn: () => void): HTMLButtonElement => {
    const b = document.createElement("button");
    b.textContent = text;
    Object.assign(b.style, {
      background: "rgba(10,15,26,0.8)",
      color: "#dce6f5",
      border: "1px solid rgba(220,230,245,0.4)",
      padding: "6px 12px",
      cursor: "pointer",
      font: "inherit",
    });
    b.addEventListener("click", fn);
    ui.appendChild(b);
    return b;
  };
  const prevBtn = mkBtn("◀", () => {});
  prevBtn.setAttribute("aria-label", "Previous piece");
  prevBtn.title = "Previous piece";
  const nextBtn = mkBtn("▶", () => {});
  nextBtn.setAttribute("aria-label", "Next piece");
  nextBtn.title = "Next piece";
  const jumpInput = document.createElement("input");
  jumpInput.placeholder = "piece # or id…";
  jumpInput.setAttribute("aria-label", "Piece number or id");
  jumpInput.autocomplete = "off";
  jumpInput.spellcheck = false;
  Object.assign(jumpInput.style, {
    background: "rgba(10,15,26,0.8)",
    color: "#dce6f5",
    border: "1px solid rgba(220,230,245,0.4)",
    padding: "6px 8px",
    width: "130px",
    font: "inherit",
  });
  ui.appendChild(jumpInput);
  const status = document.createElement("span");
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  status.setAttribute("aria-label", "Selected piece");
  Object.assign(status.style, {
    color: "#dce6f5",
    alignSelf: "center",
    padding: "0 8px",
    // Stable toolbar (USER): the piece name sits in a FIXED-width center
    // slot so the outer buttons never shift as names change length.
    width: "240px",
    textAlign: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: "0 0 auto",
  });
  status.textContent = "click a piece";
  ui.appendChild(status);
  document.body.appendChild(ui);

  // CUA-readable selected-piece facts. This intentionally stays compact so it
  // does not cover the mesh while keeping the exact proxy composition in the
  // accessibility tree and in screenshots used for collision audits.
  const auditFacts = document.createElement("pre");
  auditFacts.className = "piece-bench-facts";
  auditFacts.setAttribute("aria-label", "Selected piece collision facts");
  Object.assign(auditFacts.style, {
    position: "fixed",
    right: "12px",
    bottom: "58px",
    zIndex: "12",
    margin: "0",
    maxWidth: "min(420px, 42vw)",
    padding: "7px 9px",
    background: "rgba(10,15,26,0.82)",
    color: "#b9c8dd",
    border: "1px solid rgba(220,230,245,0.25)",
    font: "500 10px/1.35 ui-monospace, Menlo, monospace",
    whiteSpace: "pre-wrap",
    pointerEvents: "none",
  });
  document.body.appendChild(auditFacts);

  // Keep the diagnostic controls usable on phone-sized windows. The bench
  // is an audit surface, so clipped controls defeat its primary purpose.
  const responsiveStyle = document.createElement("style");
  responsiveStyle.textContent = `
    @media (max-width: 600px) {
      .piece-bench-toolbar {
        left: 8px !important;
        right: 8px !important;
        bottom: 8px !important;
        width: auto !important;
        transform: none !important;
        flex-wrap: wrap !important;
        justify-content: center !important;
      }
      .piece-bench-toolbar [role="status"] {
        order: -1;
        width: auto !important;
        flex: 1 0 calc(100% - 16px) !important;
      }
      .piece-bench-toolbar input {
        width: auto !important;
        min-width: 90px;
        flex: 1 1 120px;
      }
      .piece-bench-facts {
        left: 8px !important;
        right: 8px !important;
        bottom: 118px !important;
        max-width: none !important;
        max-height: 25vh;
        overflow: hidden;
      }
    }
  `;
  document.head.appendChild(responsiveStyle);

  let showCollision = false;
  const collisionBtn = mkBtn("collision: off", () => {
    showCollision = !showCollision;
    collisionBtn.textContent = `collision: ${showCollision ? "on" : "off"}`;
    for (const p of plots) p.overlay.visible = showCollision;
  });

  // Marble sim on the selected plot (raw physics — no autopilot; the
  // geometry answers honestly).
  let engine: PhysicsEngine | null = null;
  let physModule: PhysicsModule | null = null;
  let simPlot: Plot | null = null;
  let simT = 0;
  const simMarbles: THREE.Mesh[] = [];
  const marbleMat = new THREE.MeshStandardMaterial({
    color: 0x9adcff,
    roughness: 0.3,
  });
  const simNearest: number[] = [];
  const clearSim = () => {
    engine?.dispose();
    engine = null;
    for (const m of simMarbles) root.remove(m);
    simMarbles.length = 0;
    simNearest.length = 0;
    simPlot = null;
  };
  const simSpawn = (i: number) => {
    if (!engine || !simPlot) return;
    const rail = simPlot.rail;
    const t0 = rail.tans[0];
    const lane = (i - 2) * 0.12;
    const nl = Math.hypot(t0[2], t0[0]) || 1;
    engine.spawnMarble(i, [
      rail.pts[0][0] + (t0[2] / nl) * lane,
      rail.pts[0][1] + 0.3 + i * 0.05,
      rail.pts[0][2] + (-t0[0] / nl) * lane,
    ], [t0[0] * 2.2, 0, t0[2] * 2.2]);
    simNearest[i] = 0;
  };
  mkBtn("simulate", async () => {
    if (!selected) {
      status.textContent = "select a piece first";
      return;
    }
    if (!physModule) {
      try {
        const factory =
          (await import(new URL("/dist/box3d.mjs", location.href).href))
            .default as () => Promise<PhysicsModule>;
        physModule = await factory();
      } catch (err) {
        status.textContent = "physics unavailable";
        console.warn("[bench] physics module:", err);
        return;
      }
    }
    clearSim();
    engine = new PhysicsEngine(physModule);
    // Proxies live in plot-local space — the engine world IS this plot.
    engine.addSegment(0, selected.proxies);
    simPlot = selected;
    for (let i = 0; i < 5; i++) {
      simSpawn(i);
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(MARBLE_RADIUS, 14, 10),
        marbleMat,
      );
      root.add(mesh);
      simMarbles.push(mesh);
    }
    simT = 0;
    status.textContent = `simulating ${selected.label}`;
  });

  mkBtn("copy info", () => {
    if (!selected) {
      status.textContent = "select a piece first";
      return;
    }
    const p = selected;
    const info = diagnosticInfo(p);
    navigator.clipboard?.writeText(JSON.stringify(info, null, 2))
      .then(() => status.textContent = `copied ${p.label}`)
      .catch(() => status.textContent = "clipboard blocked");
  });

  // Pick a plot by nearest origin on click.
  let onPick: (plot: Plot) => void = () => {};
  const ray = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  ctx.renderer.domElement.addEventListener("pointerdown", (e: PointerEvent) => {
    ndc.set(
      (e.clientX / innerWidth) * 2 - 1,
      -(e.clientY / innerHeight) * 2 + 1,
    );
    ray.setFromCamera(ndc, ctx.camera);
    const hits = ray.intersectObjects(
      plots.map((p) => p.group),
      true,
    );
    let plot: Plot | undefined;
    if (hits.length > 0) {
      let obj: THREE.Object3D | null = hits[0].object;
      while (obj && !plots.some((p) => p.group === obj)) obj = obj.parent;
      plot = plots.find((p) => p.group === obj);
    } else {
      // Miss-tolerant: intersect the ground plane, take the nearest plot
      // origin within half a grid cell.
      const t = -ray.ray.origin.y / ray.ray.direction.y;
      if (!(t > 0)) return;
      const gx = ray.ray.origin.x + ray.ray.direction.x * t;
      const gz = ray.ray.origin.z + ray.ray.direction.z * t;
      let best = Infinity;
      for (const p of plots) {
        const d = Math.hypot(p.origin.x - gx, p.origin.z - gz);
        if (d < best) {
          best = d;
          plot = p;
        }
      }
      if (best > 8) return;
    }
    if (!plot) return;
    onPick(plot);
  });

  // Sim stepping: the REAL wishdir autopilot steers each marble across
  // the piece (USER: raw physics just fell into the void on curves) —
  // nearest station on the plot's rail, autopilotForce, per-marble
  // personality. Fallen marbles respawn at the entry, so the piece gets
  // continuous traffic for the whole run.
  ctx.onFrame.add((dt) => {
    if (!engine || !simPlot) return;
    simT += dt;
    const rail = simPlot.rail;
    engine.step(Math.min(dt, 0.05), () => {
      for (let i = 0; i < simMarbles.length; i++) {
        const st = engine!.state(i);
        let best = simNearest[i] ?? 0;
        let bestD = Infinity;
        const lo = Math.max(0, best - 20);
        const hi = Math.min(rail.pts.length - 1, best + 30);
        for (let k = lo; k <= hi; k++) {
          const r = rail.pts[k];
          const d = (r[0] - st.pos[0]) ** 2 + (r[1] - st.pos[1]) ** 2 +
            (r[2] - st.pos[2]) ** 2;
          if (d < bestD) {
            bestD = d;
            best = k;
          }
        }
        simNearest[i] = best;
        const f = autopilotForce(
          rail,
          best,
          st.pos,
          st.vel,
          engine!.massOf(i),
          null,
          pilotFor("bench", i),
        );
        if (f) engine!.force(i, f[0], f[1], f[2]);
      }
    });
    const floorY = rail.pts.reduce((a, p) => Math.min(a, p[1]), Infinity);
    for (let i = 0; i < simMarbles.length; i++) {
      const st = engine.state(i);
      if (st.pos[1] < floorY - 4) simSpawn(i);
      simMarbles[i].position.set(
        simPlot.origin.x + st.pos[0],
        simPlot.origin.y + st.pos[1],
        simPlot.origin.z + st.pos[2],
      );
      simMarbles[i].quaternion.set(...st.quat);
    }
    if (simT > 15) clearSim();
  });

  // 'x' toggles collision ('c' belongs to the camera-mode toggle).
  addEventListener("keydown", (ev) => {
    if (ev.key === "x" || ev.key === "X") collisionBtn.click();
  });

  const box = new THREE.Box3().setFromObject(root);
  fitShadowsTo(ctx.sun, box);
  ctx.scene.fog = null;
  // Dark void backdrop (USER): pieces, collision wires and rail dots
  // read far better than on the daylight sky.
  ctx.scene.background = new THREE.Color(0x0b1018);
  const cams = createCameras(ctx, null, null);
  ctx.onFrame.add((dt) => cams.update(dt));

  // ---- single-piece focus (USER): orbit cam per piece + navigation ------
  let current = -1;
  const focus = (i: number) => {
    if (plots.length === 0) return;
    current = ((i % plots.length) + plots.length) % plots.length;
    const plot = plots[current];
    const compact = innerWidth <= 600;
    for (const p of plots) {
      if (p.labelSprite) p.labelSprite.visible = !compact;
    }
    selected = plot;
    marker.visible = true;
    marker.position.set(plot.origin.x, 0.03, plot.origin.z);
    const pbox = new THREE.Box3().setFromObject(plot.group);
    if (!compact && plot.labelSprite) pbox.expandByObject(plot.labelSprite);
    // Empty bounds must not strand the camera on the previous piece
    // (the cross-over audit blocker) — frame the plot origin instead.
    if (pbox.isEmpty()) {
      pbox.setFromCenterAndSize(
        plot.origin.clone().add(new THREE.Vector3(0, 1, 0)),
        new THREE.Vector3(6, 4, 6),
      );
    }
    // A one-cell piece frames too tight for orbit's min distance — pad.
    pbox.expandByScalar(1.2);
    cams.frame(pbox);
    jumpInput.value = String(current + 1);
    status.textContent = `${current + 1}/${plots.length}  ${plot.label}`;
    const info = diagnosticInfo(plot);
    const proxyKinds = info.proxies.map((p) =>
      "verts" in p ? `mesh(${p.verts}v)` : p.kind
    ).join(", ");
    auditFacts.textContent = [
      `piece ${current + 1}/${plots.length}: ${plot.label}`,
      `family=${info.variant.family} moves=${info.variant.moves}`,
      `meshBBox=${JSON.stringify(info.meshBBox)}`,
      `proxies=${proxyKinds || "none"} railStations=${info.rail.stations}`,
      ...(info.phantom
        ? ["phantom (noMesh) — ghosted HOST mesh shown for comparison"]
        : []),
      ...(info.meshValid ? [] : ["⚠ MESH BOUNDS EMPTY — not evaluable"]),
    ].join("\n");
  };
  const jump = (q: string): boolean => {
    const needle = q.trim().toLowerCase();
    if (!needle) return false;
    if (/^\d+$/.test(needle)) {
      const ordinal = Number(needle);
      if (ordinal >= 1 && ordinal <= plots.length) {
        focus(ordinal - 1);
        return true;
      }
      status.textContent = `piece number must be 1–${plots.length}`;
      return false;
    }
    const at = plots.findIndex((p) => p.label.toLowerCase().includes(needle));
    if (at < 0) {
      status.textContent = `no piece matching "${q}"`;
      return false;
    }
    focus(at);
    return true;
  };
  prevBtn.addEventListener("click", () => focus(current - 1));
  nextBtn.addEventListener("click", () => focus(current + 1));
  jumpInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter") jump(jumpInput.value);
    ev.stopPropagation();
  });
  addEventListener("keydown", (ev) => {
    if (document.activeElement === jumpInput) return;
    if (ev.key === "ArrowLeft") focus(current - 1);
    if (ev.key === "ArrowRight") focus(current + 1);
  });
  addEventListener("resize", () => focus(current));
  // Clicking a plot focuses it too.
  onPick = (plot) => focus(plots.indexOf(plot));

  // CUA path: scripted navigation + bench state for harnesses.
  (globalThis as unknown as Record<string, unknown>).benchGo = (d: number) =>
    focus(current + (d | 0));
  (globalThis as unknown as Record<string, unknown>).benchJump = (id: string) =>
    jump(String(id));
  installCua(ctx, () => ({
    mode: "bench",
    index: current,
    total: plots.length,
    piece: current >= 0 ? plots[current].label : null,
    collision: showCollision,
    simulating: simPlot ? simPlot.label : null,
    diagnostic: current >= 0 ? diagnosticInfo(plots[current]) : null,
  }));

  focus(0);
}

function makeBenchLabel(text: string): THREE.Sprite | null {
  const canvas = document.createElement("canvas");
  const g = canvas.getContext("2d");
  if (!g) return null;
  canvas.width = 512;
  canvas.height = 56;
  g.font = "600 30px ui-monospace, Menlo, monospace";
  g.textAlign = "center";
  g.textBaseline = "middle";
  g.fillStyle = "#e8eef8";
  g.fillText(text, 256, 28);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      depthWrite: false,
    }),
  );
  sprite.scale.set(6.4, 0.7, 1);
  return sprite;
}
